/* ---------------------------------------------------------------------------
   Construction Monitoring: one structure's site record, as charts.

   The same four lists the other site-record pages print — works, daily returns,
   incidents, and the programme they are measured against — read six ways:

     · the headline figures;
     · planned against actual, cumulative, by day (the S-curve);
     · how far through each part of the structure is, against its plan;
     · what the site produced, week by week, one chart per measure;
     · each concrete mix's strength, result by result, against what it needed;
     · who was on site and what the weather did;
     · how late each finished activity finished;
     · where the record stands in review, and when anything went wrong.

   NOTHING IS COMPUTED HERE THAT src/data/variance.ts ALREADY DECIDES. Percent
   complete is weighted by planned duration, counts an activity on its declared
   finish, counts only accepted entries, and comes as two bounds — declared, and
   started or declared — exactly as the Scheduler's variance page and the period
   report state it. A chart that disagreed with the report beside it would be
   the one a reader stopped trusting.

   QUANTITIES ARE CHARTED PER MEASURE AND NEVER SUMMED ACROSS UNITS, for the
   reason src/screens/works.ts gives: cubic metres of concrete and tonnes of
   steel added together measure nothing.

   Hand-drawn SVG rather than a charting library: six charts do not justify a
   dependency, and every colour comes from the chart tokens in tokens.css so the
   two themes are chosen, not flipped.
   --------------------------------------------------------------------------- */

import { h, type Child } from '../core/dom.ts';
import type { State } from '../core/store.ts';
import { card, cardHead, emptyState, statTile } from '../components/widgets.ts';
import { chartBox, hbar, hideTip, niceTicks, r1, scale, segment, showTip, sv, vbar } from '../components/chart.ts';
import { prettyDate } from '../data/derive.ts';
import { addDays } from '../data/periods.ts';
import { incidentLabel, weatherLabel } from '../data/siteReturn.ts';
import { completionOf, signedDays, varianceFor, type Baseline, type CountableEntry, type VarianceRow } from '../data/variance.ts';
import { strengthByMix, type MixSeries } from '../data/siteQuality.ts';
import type { ProgressEntry, SiteIncident, SiteReturn } from '../data/types.ts';

/** Today, as a calendar day. The one place this module reads the clock. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Only accepted entries count toward progress — the rule the variance page keeps. */
function countable(s: Readonly<State>): CountableEntry[] {
  return s.progress
    .filter((e) => e.state === 'accepted' && !e.pending)
    .map((e) => ({ component: e.component, componentId: e.componentId ?? null, occurredOn: e.occurredOn }));
}

export function progressCharts(s: Readonly<State>): HTMLElement {
  if (s.progressRead === 'unreachable') return emptyState('The construction record could not be read.');
  if (s.progressRead === 'unknown') return emptyState('The construction record has not been read yet.');

  const works = s.progress.filter((e) => !e.pending);
  const returns = s.siteReturns.filter((r) => !r.pending);
  const incidents = s.incidents.filter((r) => !r.pending);
  const mixes = strengthByMix(s.tests);
  if (s.baseline === null && works.length === 0 && returns.length === 0 && mixes.length === 0) {
    return emptyState('Nothing has been recorded on this structure to chart.');
  }

  const now = today();
  const start = firstDay(s.baseline, works, returns);
  const rows = s.baseline ? varianceFor(s.baseline, s.matches, countable(s)) : [];
  const plan = s.baseline && rows.some((r) => r.plannedDays > 0) ? planCurves(s.baseline, rows, now) : null;

  return h(
    'div',
    { class: 'pcharts' },
    plan ? headline(plan, rows, incidents) : null,
    plan ? sCurve(plan) : null,
    plan && s.baseline ? partsChart(s.baseline, rows, now) : null,
    outputCharts(works, start, now),
    strengthChart(mixes),
    returns.length ? labourChart(returns, works, start, now) : null,
    plan ? slipChart(rows) : null,
    statusChart(works, returns, incidents, start, now),
  );
}

/** Day zero: the programme's start, or the first thing recorded where there is no programme. */
function firstDay(baseline: Baseline | null, works: ProgressEntry[], returns: SiteReturn[]): string {
  if (baseline) return baseline.projectStart;
  const days = [...works.map((e) => e.occurredOn), ...returns.map((r) => r.returnedOn)].sort();
  return days[0] ?? today();
}

/* --------------------------- the curves --------------------------- */

interface Plan {
  start: string;
  /** Contract day of the planned finish. */
  end: number;
  /** Contract day today, clamped to the programme. */
  now: number;
  /** Cumulative percentages, one per contract day from 0 to `end`. */
  planned: number[];
  /** To `now` only — the future has not happened. */
  declared: number[];
  started: number[];
  name: string;
}

/**
 * Planned, declared and started, cumulative, per contract day.
 *
 * AN ACTIVITY COUNTS ON ITS FINISH, as in `progressCurve`: the lines are
 * staircases, which is the honest appearance of a programme measured in
 * completed activities. Started counts from the first accepted entry, or from
 * the declared finish where nothing accepted was matched to it.
 */
function planCurves(baseline: Baseline, rows: VarianceRow[], now: string): Plan {
  const start = baseline.projectStart;
  const work = rows.filter((r) => r.plannedDays > 0);
  const total = work.reduce((n, r) => n + r.plannedDays, 0);
  const end = Math.max(0, ...work.map((r) => signedDays(start, r.plannedFinish)));
  const nowDay = Math.max(0, Math.min(end, signedDays(start, now)));

  const add = (into: number[], day: number | null, w: number) => {
    if (day === null) return;
    into[Math.max(0, day)] = (into[Math.max(0, day)] ?? 0) + w;
  };
  const p: number[] = new Array(end + 1).fill(0);
  const d: number[] = new Array(end + 1).fill(0);
  const st: number[] = new Array(end + 1).fill(0);
  for (const r of work) {
    add(p, Math.min(end, signedDays(start, r.plannedFinish)), r.plannedDays);
    const done = r.completedOn ? signedDays(start, r.completedOn) : null;
    add(d, done, r.plannedDays);
    const began = [r.firstEntry, r.completedOn].filter((x): x is string => x !== null).sort()[0];
    if (r.state !== 'unmatched' && r.state !== 'not-started' && began) add(st, signedDays(start, began), r.plannedDays);
  }
  const cumulative = (a: number[], upTo: number) => {
    let run = 0;
    return a.slice(0, upTo + 1).map((w) => {
      run += w;
      return total === 0 ? 0 : Math.round((run / total) * 1000) / 10;
    });
  };
  return {
    start,
    end,
    now: nowDay,
    planned: cumulative(p, end),
    declared: cumulative(d, nowDay),
    started: cumulative(st, nowDay),
    name: baseline.name,
  };
}

/**
 * How many days behind the programme the declared work is: today, less the
 * day on which the plan expected today's declared percentage. Earned schedule,
 * on the same staircase. Negative is ahead.
 */
function daysBehind(plan: Plan): { days: number; asOf: number } | null {
  const done = plan.declared[plan.now] ?? 0;
  if (done <= 0) return null;
  const asOf = plan.planned.findIndex((v) => v >= done);
  if (asOf < 0) return null;
  return { days: plan.now - asOf, asOf };
}

/* --------------------------- headline --------------------------- */

function headline(plan: Plan, rows: VarianceRow[], incidents: SiteIncident[]): HTMLElement {
  const done = completionOf(rows);
  const behind = daysBehind(plan);
  const open = incidents.filter((i) => !i.closed).length;
  return h(
    'div',
    { class: 'pcharts__kpis' },
    statTile({
      label: 'Declared complete',
      value: fmt(done.declaredPercent),
      unit: '%',
      sub: `${fmt(done.startedPercent)}% started or complete`,
    }),
    statTile({
      label: 'Planned by today',
      value: fmt(plan.planned[plan.now] ?? 0),
      unit: '%',
      sub: `Day ${plan.now + 1} of ${plan.end + 1}`,
    }),
    statTile({
      label: behind && behind.days < 0 ? 'Ahead of programme' : 'Behind programme',
      value: behind ? String(Math.abs(behind.days)) : '—',
      unit: behind ? 'days' : '',
      sub: behind ? `Where the plan stood on ${prettyDate(addDays(plan.start, behind.asOf))}` : 'Nothing declared complete yet',
    }),
    statTile({
      label: 'Open incidents',
      value: String(open),
      unit: '',
      sub: `${incidents.length} reported`,
    }),
  );
}

/* --------------------------- the S-curve --------------------------- */

/**
 * The S-curve as at a day, for the period report (LC-55): the Charts page's
 * own chart, measured as at the period's end. Null where nothing is planned.
 */
export function sCurveChart(baseline: Baseline, rows: readonly VarianceRow[], asOf: string): HTMLElement | null {
  if (!rows.some((r) => r.plannedDays > 0)) return null;
  return sCurve(planCurves(baseline, [...rows], asOf), false);
}

function sCurve(plan: Plan, captioned = true): HTMLElement {
  const W = WIDE;
  const H = 260;
  const m = { l: 40, r: 16, t: 14, b: 26 };
  const x = scale(0, plan.end, m.l, W - m.r);
  const y = scale(0, 100, H - m.b, m.t);

  const band =
    plan.started.length > 0
      ? `M${stepPoints(plan.started, x, y).join('L')}L${stepPoints(plan.declared, x, y).reverse().join('L')}Z`
      : '';

  const svg = sv(
    'svg',
    { viewBox: `0 0 ${W} ${H}`, class: 'pchart__svg', role: 'img', 'aria-label': curveSummary(plan) },
    ...yGrid([0, 25, 50, 75, 100], y, m.l, W - m.r, (v) => `${v}%`),
    ...monthAxis(plan.start, plan.end, x, H - m.b),
    band ? sv('path', { d: band, class: 'pchart__wash' }) : null,
    sv('path', { d: `M${stepPoints(plan.planned, x, y).join('L')}`, class: 'pchart__line pchart__line--plan' }),
    sv('path', { d: `M${stepPoints(plan.declared, x, y).join('L')}`, class: 'pchart__line pchart__line--actual' }),
    sv('line', { x1: x(plan.now), x2: x(plan.now), y1: m.t, y2: H - m.b, class: 'pchart__today' }),
    sv('text', { x: x(plan.now) + 4, y: m.t + 9, class: 'pchart__note' }, 'Today'),
    sv('circle', { cx: x(plan.now), cy: y(plan.declared[plan.now] ?? 0), r: 4, class: 'pchart__dot pchart__dot--actual' }),
    sv('line', { x1: 0, x2: 0, y1: m.t, y2: H - m.b, class: 'pchart__cross', visibility: 'hidden' }),
  );

  /* A crosshair over the whole plot rather than a target per point: two
     hundred and forty-five days are too many marks to aim at. */
  const onMove = (e: PointerEvent, box: HTMLElement) => {
    const el = box.querySelector('svg');
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const day = Math.round(((px - m.l) / (W - m.l - m.r)) * plan.end);
    if (day < 0 || day > plan.end) return hideTip(box);
    const cross = box.querySelector('.pchart__cross');
    cross?.setAttribute('x1', String(x(day)));
    cross?.setAttribute('x2', String(x(day)));
    cross?.setAttribute('visibility', 'visible');
    const lines = [`${prettyDate(addDays(plan.start, day))} · day ${day + 1}`, `Planned ${fmt(plan.planned[day] ?? 0)}%`];
    if (day <= plan.now) {
      lines.push(`Declared ${fmt(plan.declared[day] ?? 0)}%`, `Started or complete ${fmt(plan.started[day] ?? 0)}%`);
    }
    showTip(box, e, lines);
  };

  return chartCard(
    'Planned against actual',
    captioned ? `Cumulative, weighted by planned duration · ${plan.name}` : undefined,
    legend([
      { label: 'Planned', kind: 'line-plan' },
      { label: 'Declared complete', kind: 'line-actual' },
      { label: 'Started', kind: 'wash' },
    ]),
    chartBox(svg, onMove),
  );
}

function curveSummary(plan: Plan): string {
  return (
    `Cumulative progress. By day ${plan.now + 1}, ${fmt(plan.planned[plan.now] ?? 0)}% was planned and ` +
    `${fmt(plan.declared[plan.now] ?? 0)}% is declared complete.`
  );
}

/** A staircase through cumulative values: flat along each day, rising at its end. */
function stepPoints(values: number[], x: (d: number) => number, y: (v: number) => number): string[] {
  const pts: string[] = [];
  values.forEach((v, d) => {
    if (d > 0) pts.push(`${r1(x(d))},${r1(y(values[d - 1]!))}`);
    pts.push(`${r1(x(d))},${r1(y(v))}`);
  });
  return pts;
}

/* --------------------------- by part --------------------------- */

interface Part {
  name: string;
  planned: number;
  declared: number;
  started: number;
}

/**
 * The programme's parts — the groups under its root — each as declared and
 * started against what was planned to be finished by today.
 */
function partsOf(baseline: Baseline, rows: VarianceRow[], now: string): Part[] {
  const acts = baseline.activities;
  const roots = acts.filter((a) => a.parentId === null);
  const topId = roots.length === 1 && roots[0]!.isGroup ? roots[0]!.activityId : null;
  const parts = acts.filter((a) => a.isGroup && a.parentId === topId);
  const parent = new Map(acts.map((a) => [a.activityId, a.parentId]));
  const partOf = (id: string): string | null => {
    let at: string | null = id;
    while (at !== null) {
      const up: string | null = parent.get(at) ?? null;
      if (up === topId) return at;
      at = up;
    }
    return null;
  };
  return parts
    .map((p) => {
      const mine = rows.filter((r) => r.plannedDays > 0 && partOf(r.activityId) === p.activityId);
      const total = mine.reduce((n, r) => n + r.plannedDays, 0);
      const pct = (keep: (r: VarianceRow) => boolean) =>
        total === 0 ? 0 : (mine.filter(keep).reduce((n, r) => n + r.plannedDays, 0) / total) * 100;
      return {
        name: p.name,
        planned: pct((r) => r.plannedFinish <= now),
        declared: pct((r) => r.state === 'complete'),
        started: pct((r) => r.state === 'complete' || r.state === 'in-progress'),
      };
    });
}

function partsChart(baseline: Baseline, rows: VarianceRow[], now: string): HTMLElement | null {
  const parts = partsOf(baseline, rows, now);
  if (parts.length === 0) return null;
  const W = WIDE;
  const rowH = 28;
  const m = { l: 170, r: 120, t: 6, b: 22 };
  const H = m.t + parts.length * rowH + m.b;
  const x = scale(0, 100, m.l, W - m.r);
  const barH = 12;

  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': 'Percent complete by part of the structure, against the plan for today.',
    },
    ...[0, 25, 50, 75, 100].map((v) =>
      sv(
        'g',
        {},
        sv('line', { x1: x(v), x2: x(v), y1: m.t, y2: H - m.b, class: 'pchart__grid' }),
        sv('text', { x: x(v), y: H - 6, class: 'pchart__tick', 'text-anchor': 'middle' }, `${v}%`),
      ),
    ),
    ...parts.map((p, i) => {
      const cy = m.t + i * rowH + rowH / 2;
      const tip = [p.name, `Declared ${fmt1(p.declared)}%`, `Started ${fmt1(p.started)}%`, `Planned by today ${fmt1(p.planned)}%`].join('\n');
      return sv(
        'g',
        { 'data-tip': tip },
        sv('rect', { x: 0, y: cy - rowH / 2, width: W, height: rowH, class: 'pchart__hit' }),
        sv('text', { x: m.l - 10, y: cy + 4, class: 'pchart__label', 'text-anchor': 'end' }, p.name),
        sv('rect', { x: x(0), y: cy - barH / 2, width: x(100) - x(0), height: barH, rx: 4, class: 'pchart__track' }),
        p.started > 0
          ? sv('path', { d: hbar(x(0), cy - barH / 2, x(p.started) - x(0), barH), class: 'pchart__bar pchart__bar--wash' })
          : null,
        p.declared > 0
          ? sv('path', { d: hbar(x(0), cy - barH / 2, x(p.declared) - x(0), barH), class: 'pchart__bar pchart__bar--actual' })
          : null,
        sv('line', { x1: x(p.planned), x2: x(p.planned), y1: cy - barH / 2 - 4, y2: cy + barH / 2 + 4, class: 'pchart__target' }),
        sv('text', { x: W - m.r + 12, y: cy + 4, class: 'pchart__value' }, `${fmt1(p.declared)}%`),
        sv('text', { x: W - m.r + 52, y: cy + 4, class: 'pchart__tick' }, `plan ${fmt1(p.planned)}%`),
      );
    }),
  );

  return chartCard(
    'Progress by part', undefined,
    legend([
      { label: 'Declared complete', kind: 'bar-actual' },
      { label: 'Started', kind: 'wash' },
      { label: 'Planned by today', kind: 'target' },
    ]),
    chartBox(svg),
  );
}

/* --------------------------- weekly output --------------------------- */

interface Measure {
  title: string;
  unit: string;
  activities: readonly string[];
}

/**
 * The measures a bridge site is read by, each with the activities that produce
 * it. Formwork is counted when erected and not when struck, which is the same
 * area twice.
 */
const MEASURES: readonly Measure[] = [
  {
    title: 'Concrete placed',
    unit: 'm³',
    activities: ['pile-concreted', 'pilecap-poured', 'footing-poured', 'concrete-poured', 'deck-poured'],
  },
  { title: 'Reinforcement fixed', unit: 't', activities: ['rebar-fixed', 'pile-cage'] },
  { title: 'Formwork erected', unit: 'm²', activities: ['formwork-erected'] },
  { title: 'Excavation', unit: 'm³', activities: ['excavation'] },
];

function outputCharts(works: ProgressEntry[], start: string, now: string): HTMLElement | null {
  const weeks = Math.max(1, Math.floor(Math.max(0, signedDays(start, now)) / 7) + 1);
  const panels = MEASURES.map((ms) => {
    const mine = works.filter((e) => ms.activities.includes(e.activity) && e.unit === ms.unit && e.quantity !== null);
    if (mine.length === 0) return null;
    const byWeek: { qty: number; n: number }[] = Array.from({ length: weeks }, () => ({ qty: 0, n: 0 }));
    for (const e of mine) {
      const w = Math.floor(signedDays(start, e.occurredOn) / 7);
      if (w < 0 || w >= weeks) continue;
      byWeek[w]!.qty += e.quantity!;
      byWeek[w]!.n += 1;
    }
    const total = byWeek.reduce((n, b) => n + b.qty, 0);
    return h(
      'div',
      { class: 'pchart__multiple' },
      h(
        'div',
        { class: 'pchart__mhead' },
        h('span', { class: 'pchart__mtitle', text: ms.title }),
        h('span', { class: 'pchart__mhint', text: `${fmt(total)} ${ms.unit} in total` }),
      ),
      chartBox(weeklyColumns(byWeek, start, ms)),
    );
  }).filter((p) => p !== null);
  if (panels.length === 0) return null;
  return chartCard('Weekly output', undefined, null, h('div', { class: 'pchart__grid2' }, ...panels));
}

function weeklyColumns(byWeek: { qty: number; n: number }[], start: string, ms: Measure): SVGElement {
  const W = 440;
  const H = 170;
  const m = { l: 40, r: 8, t: 16, b: 22 };
  const max = Math.max(...byWeek.map((b) => b.qty));
  const ticks = niceTicks(max);
  const top = ticks[ticks.length - 1]!;
  const y = scale(0, top, H - m.b, m.t);
  const slot = (W - m.l - m.r) / byWeek.length;
  const bw = Math.min(24, Math.max(2, slot - 2));
  const peak = byWeek.findIndex((b) => b.qty === max);
  return sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `${ms.title} per week, peaking at ${fmt(max)} ${ms.unit}.`,
    },
    ...yGrid(ticks, y, m.l, W - m.r, (v) => fmt(v)),
    ...byWeek.map((b, i) => {
      const x0 = m.l + i * slot + (slot - bw) / 2;
      const tip = `Week of ${prettyDate(addDays(start, i * 7))}\n${fmt(b.qty)} ${ms.unit} · ${b.n} ${b.n === 1 ? 'entry' : 'entries'}`;
      return sv(
        'g',
        { 'data-tip': tip },
        sv('rect', { x: m.l + i * slot, y: m.t, width: slot, height: H - m.b - m.t, class: 'pchart__hit' }),
        b.qty > 0 ? sv('path', { d: vbar(x0, y(0), bw, y(0) - y(b.qty)), class: 'pchart__bar pchart__bar--actual' }) : null,
      );
    }),
    max > 0
      ? sv(
          'text',
          { x: m.l + peak * slot + slot / 2, y: y(max) - 4, class: 'pchart__value', 'text-anchor': 'middle' },
          fmt(max),
        )
      : null,
    ...weekAxis(byWeek.length, start, (i) => m.l + i * slot + slot / 2, H - m.b),
  );
}

/* --------------------------- concrete strength (LC-53) --------------------------- */

/**
 * One chart per mix: every result as a point, in the order tested, against the
 * strength it needed at its age — a tick at each result, since a 7-day result
 * needs less than a 28-day one. A failure is drawn as a ring. The line joins
 * the results at the mix's greatest age, which is the trend a reader wants.
 */
function strengthChart(mixes: MixSeries[]): HTMLElement | null {
  if (mixes.length === 0) return null;
  const panels = mixes.map((m) => {
    const failed = m.results.filter((t) => !t.passed).length;
    return h(
      'div',
      { class: 'pchart__multiple', 'data-mix': m.mix },
      h(
        'div',
        { class: 'pchart__mhead' },
        h('span', { class: 'pchart__mtitle', text: m.mix }),
        h('span', {
          class: 'pchart__mhint',
          text: `${m.results.length} ${m.results.length === 1 ? 'result' : 'results'}${failed ? ` · ${failed} failed` : ''}`,
        }),
      ),
      chartBox(mixStrength(m)),
    );
  });
  return chartCard(
    'Concrete strength', undefined,
    legend([
      { label: 'Result', kind: 'dot' },
      { label: 'Failed', kind: 'ring' },
      { label: 'Required', kind: 'target' },
    ]),
    h('div', { class: 'pchart__grid2' }, ...panels),
  );
}

function mixStrength(m: MixSeries): SVGElement {
  const W = 440;
  const H = 170;
  const pad = { l: 40, r: 12, t: 16, b: 22 };
  const top = niceTicks(Math.max(1, ...m.results.map((t) => Math.max(t.strengthMpa ?? 0, t.requiredMpa ?? 0))));
  const y = scale(0, top[top.length - 1]!, H - pad.b, pad.t);
  const n = m.results.length;
  const slot = (W - pad.l - pad.r) / n;
  const x = (i: number) => pad.l + i * slot + slot / 2;
  const oldest = Math.max(...m.results.map((t) => t.ageDays ?? 0));
  const trend = m.results
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => (t.ageDays ?? 0) === oldest)
    .map(({ t, i }) => `${r1(x(i))},${r1(y(t.strengthMpa!))}`);
  return sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `${m.mix}: ${n} results, ${m.results.filter((t) => !t.passed).length} below the strength required.`,
    },
    ...yGrid(top, y, pad.l, W - pad.r, (v) => fmt(v)),
    trend.length > 1 ? sv('path', { d: `M${trend.join('L')}`, class: 'pchart__line pchart__line--actual' }) : null,
    ...m.results.map((t, i) =>
      sv(
        'g',
        {
          'data-tip': [
            `${prettyDate(t.testedOn)} · ${t.ageDays ?? '?'} d`,
            `${fmt(t.strengthMpa!)} MPa, ${fmt(t.requiredMpa ?? 0)} required`,
            t.passed ? 'Pass' : `Fail${t.ncrRef ? ` · ${t.ncrRef}` : ''}`,
            ...(t.component ? [t.component] : []),
          ].join('\n'),
        },
        sv('rect', { x: pad.l + i * slot, y: pad.t, width: slot, height: H - pad.t - pad.b, class: 'pchart__hit' }),
        t.requiredMpa !== null
          ? sv('line', {
              x1: r1(x(i) - Math.min(8, slot / 2)),
              x2: r1(x(i) + Math.min(8, slot / 2)),
              y1: r1(y(t.requiredMpa)),
              y2: r1(y(t.requiredMpa)),
              class: 'pchart__target',
            })
          : null,
        sv('circle', {
          cx: r1(x(i)),
          cy: r1(y(t.strengthMpa!)),
          r: 4,
          class: t.passed ? 'pchart__dot pchart__dot--actual' : 'pchart__inc pchart__inc--open',
          'data-result': t.passed ? 'pass' : 'fail',
        }),
      ),
    ),
    sv('line', { x1: pad.l, x2: W - pad.r, y1: H - pad.b, y2: H - pad.b, class: 'pchart__axis' }),
  );
}

/* --------------------------- labour and weather --------------------------- */

/** Weather that is drawn, and the class it is drawn with. Clear is the ground. */
const WEATHER_CLASS: Readonly<Record<string, string>> = {
  overcast: 'wx-overcast',
  rain: 'wx-rain',
  'heavy-rain': 'wx-heavy',
  storm: 'wx-heavy',
  'extreme-heat': 'wx-heat',
};

function labourChart(returns: SiteReturn[], works: ProgressEntry[], start: string, now: string): HTMLElement {
  const W = WIDE;
  const H = 230;
  const m = { l: 40, r: 16, t: 20, b: 44 };
  const last = Math.max(0, signedDays(start, now), ...returns.map((r) => signedDays(start, r.returnedOn)));
  const days = last + 1;
  const byDay = new Map(returns.map((r) => [signedDays(start, r.returnedOn), r]));
  const stopped = new Set(works.filter((e) => e.activity === 'stoppage').map((e) => signedDays(start, e.occurredOn)));
  const max = Math.max(1, ...returns.map((r) => r.labour ?? 0));
  const ticks = niceTicks(max);
  const y = scale(0, ticks[ticks.length - 1]!, H - m.b, m.t);
  const slot = (W - m.l - m.r) / days;
  const bw = Math.max(1, Math.min(24, slot - (slot > 4 ? 1.5 : 0.5)));
  const stripY = H - m.b + 6;
  const x = (d: number) => m.l + d * slot + slot / 2;

  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `Labour on site per day, peaking at ${max}, with the weather and the days work stopped.`,
    },
    ...yGrid(ticks, y, m.l, W - m.r, (v) => fmt(v)),
    ...Array.from({ length: days }, (_, d) => {
      const r = byDay.get(d);
      const n = r?.labour ?? 0;
      const wx = r ? WEATHER_CLASS[r.weather] : undefined;
      const tip = r
        ? [
            `${prettyDate(r.returnedOn)} · day ${d + 1}`,
            `${r.labour ?? 'Not counted'} on site${r.hours !== null ? ` · ${r.hours} h` : ''}`,
            weatherLabel(r.weather),
            ...(stopped.has(d) ? ['Work stopped'] : []),
            ...(r.note.replace(/^Demonstration return\.\s*/, '') ? [r.note] : []),
          ].join('\n')
        : `${prettyDate(addDays(start, d))} · day ${d + 1}\nNo return`;
      return sv(
        'g',
        { 'data-tip': tip },
        sv('rect', { x: m.l + d * slot, y: m.t - 12, width: slot, height: H - m.t - m.b + 30, class: 'pchart__hit' }),
        n > 0
          ? sv('path', {
              d: vbar(x(d) - bw / 2, y(0), bw, y(0) - y(n), Math.min(2, bw / 2)),
              class: 'pchart__bar pchart__bar--actual',
            })
          : null,
        wx ? sv('rect', { x: m.l + d * slot, y: stripY, width: Math.max(1, slot), height: 8, class: `pchart__wx ${wx}` }) : null,
        stopped.has(d)
          ? sv('path', { d: `M${r1(x(d) - 4)},${r1(y(n) - 11)}h8l-4,6z`, class: 'pchart__stop' })
          : null,
      );
    }),
    ...monthAxis(start, last, (d) => x(d), stripY + 12, false),
  );

  return chartCard(
    'Labour and weather', undefined,
    legend([
      { label: 'On site', kind: 'bar-actual' },
      { label: 'Overcast', kind: 'wx-overcast' },
      { label: 'Rain', kind: 'wx-rain' },
      { label: 'Heavy rain or storm', kind: 'wx-heavy' },
      { label: 'Extreme heat', kind: 'wx-heat' },
      { label: 'Work stopped', kind: 'stop' },
    ]),
    chartBox(svg),
  );
}

/* --------------------------- finish slip --------------------------- */

function slipChart(rows: VarianceRow[]): HTMLElement | null {
  const done = rows
    .filter((r) => r.plannedDays > 0 && r.finishVarianceDays !== null)
    .sort((a, b) => a.plannedFinish.localeCompare(b.plannedFinish));
  if (done.length === 0) return null;
  const W = WIDE;
  const H = 220;
  const m = { l: 40, r: 16, t: 18, b: 18 };
  const worst = Math.max(1, ...done.map((r) => Math.abs(r.finishVarianceDays!)));
  const lateMax = Math.max(0, ...done.map((r) => r.finishVarianceDays!));
  const earlyMax = Math.max(0, ...done.map((r) => -r.finishVarianceDays!));
  const up = niceTicks(Math.max(lateMax, 1));
  const down = earlyMax > 0 ? niceTicks(earlyMax) : [0];
  const hi = up[up.length - 1]!;
  const lo = -down[down.length - 1]!;
  const y = scale(lo, hi, H - m.b, m.t);
  const slot = (W - m.l - m.r) / done.length;
  const bw = Math.min(24, Math.max(2, slot - 2));
  const late = done.filter((r) => r.finishVarianceDays! > 0).length;
  const early = done.filter((r) => r.finishVarianceDays! < 0).length;
  const worstIdx = done.findIndex((r) => r.finishVarianceDays === lateMax);
  const ticks = [...down.filter((v) => v > 0).map((v) => -v), ...up];

  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `${done.length} activities declared complete: ${late} late, ${early} early. Worst ${worst} days.`,
    },
    ...yGrid(ticks, y, m.l, W - m.r, (v) => (v > 0 ? `+${v}` : String(v))),
    sv('line', { x1: m.l, x2: W - m.r, y1: y(0), y2: y(0), class: 'pchart__axis' }),
    ...done.map((r, i) => {
      const v = r.finishVarianceDays!;
      const x0 = m.l + i * slot + (slot - bw) / 2;
      const tip = [
        r.name,
        `Planned finish ${prettyDate(r.plannedFinish)}`,
        `Declared ${prettyDate(r.completedOn)}`,
        v === 0 ? 'On time' : `${Math.abs(v)} ${Math.abs(v) === 1 ? 'day' : 'days'} ${v > 0 ? 'late' : 'early'}`,
      ].join('\n');
      return sv(
        'g',
        { 'data-tip': tip },
        sv('rect', { x: m.l + i * slot, y: m.t, width: slot, height: H - m.t - m.b, class: 'pchart__hit' }),
        v > 0
          ? sv('path', { d: vbar(x0, y(0), bw, y(0) - y(v)), class: 'pchart__bar pchart__bar--late' })
          : v < 0
            ? sv('path', { d: vbar(x0, y(0), bw, y(0) - y(v)), class: 'pchart__bar pchart__bar--early' })
            : sv('rect', { x: x0, y: y(0) - 1, width: bw, height: 2, class: 'pchart__ontime' }),
      );
    }),
    lateMax > 0
      ? sv(
          'text',
          {
            x: m.l + worstIdx * slot + slot / 2,
            y: y(lateMax) - 5,
            class: 'pchart__value',
            'text-anchor': worstIdx > done.length * 0.7 ? 'end' : 'middle',
          },
          `+${lateMax} · ${shortName(done[worstIdx]!)}`,
        )
      : null,
  );

  return chartCard(
    'Finish against plan',
    `${done.length} activities declared complete, in planned order · ${late} late, ${early} early`,
    legend([
      { label: 'Late', kind: 'bar-late' },
      { label: 'Early', kind: 'bar-early' },
    ]),
    chartBox(svg),
  );
}

/** What the worst slip is called in its label: the component, which names its part, where it has one. */
function shortName(r: VarianceRow): string {
  return r.component ?? r.name;
}

/* --------------------------- record status and incidents --------------------------- */

const STATE_ORDER = [
  { state: 'accepted', label: 'Verified', cls: 'pchart__bar--actual' },
  { state: 'review', label: 'In review', cls: 'pchart__bar--review' },
  { state: 'draft', label: 'Recorded', cls: 'pchart__bar--draft' },
] as const;

function statusChart(
  works: ProgressEntry[],
  returns: SiteReturn[],
  incidents: SiteIncident[],
  start: string,
  now: string,
): HTMLElement | null {
  const lists = [
    { name: 'Works entries', states: works.map((e) => e.state as string) },
    { name: 'Daily returns', states: returns.map((r) => r.state as string) },
  ].filter((l) => l.states.length > 0);
  if (lists.length === 0 && incidents.length === 0) return null;

  const W = WIDE;
  const m = { l: 130, r: 16 };
  const rowH = 30;
  const barH = 16;
  const x = scale(0, 1, m.l, W - m.r);
  const H1 = lists.length * rowH + 4;

  const bars = lists.length
    ? sv(
        'svg',
        {
          viewBox: `0 0 ${W} ${H1}`,
          class: 'pchart__svg',
          role: 'img',
          'aria-label': lists
            .map((l) => `${l.name}: ${STATE_ORDER.map((o) => `${l.states.filter((s) => s === o.state).length} ${o.label.toLowerCase()}`).join(', ')}`)
            .join('. '),
        },
        ...lists.map((l, i) => {
          const cy = 4 + i * rowH + rowH / 2;
          const total = l.states.length;
          let at = 0;
          return sv(
            'g',
            {},
            sv('text', { x: m.l - 10, y: cy + 4, class: 'pchart__label', 'text-anchor': 'end' }, l.name),
            ...STATE_ORDER.map((o, k) => {
              const n = l.states.filter((s) => s === o.state).length;
              if (n === 0) return null;
              const x0 = x(at / total);
              at += n;
              const w = Math.max(1, x(at / total) - x0 - (at < total ? 2 : 0));
              const first = k === STATE_ORDER.findIndex((q) => l.states.includes(q.state));
              const lastSeg = at === total;
              const label = String(n);
              const fits = w > label.length * 7 + 12;
              return sv(
                'g',
                { 'data-tip': `${l.name}\n${n} ${o.label.toLowerCase()} of ${total}` },
                sv('path', {
                  d: segment(x0, cy - barH / 2, w, barH, first ? 4 : 0, lastSeg ? 4 : 0),
                  class: `pchart__bar ${o.cls}`,
                }),
                fits
                  ? sv('text', { x: x0 + w / 2, y: cy + 4, class: `pchart__inbar pchart__inbar--${o.state}`, 'text-anchor': 'middle' }, label)
                  : null,
              );
            }),
          );
        }),
      )
    : null;

  const timeline = incidents.length ? incidentTimeline(incidents, start, now) : null;

  return chartCard(
    'Review and safety',
    'Where the record stands with the engineer, and every incident reported',
    lists.length
      ? legend([
          { label: 'Verified', kind: 'bar-actual' },
          { label: 'In review', kind: 'bar-review' },
          { label: 'Recorded', kind: 'bar-draft' },
          ...(incidents.length
            ? [
                { label: 'Incident, closed', kind: 'dot' as const },
                { label: 'Incident, open', kind: 'ring' as const },
              ]
            : []),
        ])
      : null,
    bars ? chartBox(bars) : null,
    timeline,
  );
}

function incidentTimeline(incidents: SiteIncident[], start: string, now: string): HTMLElement {
  const W = WIDE;
  const H = 70;
  const m = { l: 130, r: 16 };
  const last = Math.max(1, signedDays(start, now), ...incidents.map((i) => signedDays(start, i.occurredOn)));
  const x = scale(0, last, m.l, W - m.r);
  const cy = 22;
  const svg = sv(
    'svg',
    {
      viewBox: `0 0 ${W} ${H}`,
      class: 'pchart__svg',
      role: 'img',
      'aria-label': `${incidents.length} incidents reported, ${incidents.filter((i) => !i.closed).length} open.`,
    },
    sv('text', { x: m.l - 10, y: cy + 4, class: 'pchart__label', 'text-anchor': 'end' }, 'Incidents'),
    sv('line', { x1: m.l, x2: W - m.r, y1: cy, y2: cy, class: 'pchart__axis' }),
    ...incidents
      .slice()
      .sort((a, b) => a.occurredOn.localeCompare(b.occurredOn))
      .map((i) => {
        const d = signedDays(start, i.occurredOn);
        const tip = [
          `${incidentLabel(i.kind)} · ${prettyDate(i.occurredOn)}`,
          i.description,
          i.closed ? 'Closed' : 'Open',
        ].join('\n');
        return sv(
          'g',
          { 'data-tip': tip },
          sv('circle', { cx: x(d), cy, r: 12, class: 'pchart__hit' }),
          sv('circle', { cx: x(d), cy, r: 5, class: i.closed ? 'pchart__inc' : 'pchart__inc pchart__inc--open' }),
        );
      }),
    ...monthAxis(start, last, x, cy + 14, false),
  );
  return chartBox(svg);
}

/* --------------------------- the chart frame --------------------------- */

/**
 * The drawing width of a chart that spans the panel. SVG text scales with its
 * box, so this is set near the width the panel is usually drawn at, and the
 * labels come out close to the size of the text around them.
 */
const WIDE = 960;

function chartCard(title: string, hint: string | undefined, key: HTMLElement | null, ...body: Child[]): HTMLElement {
  return card({ class: 'pchart' }, cardHead(title, hint), key, h('div', { class: 'pchart__body' }, ...body));
}

type KeyKind =
  | 'line-plan'
  | 'line-actual'
  | 'wash'
  | 'bar-actual'
  | 'bar-review'
  | 'bar-draft'
  | 'bar-late'
  | 'bar-early'
  | 'target'
  | 'wx-overcast'
  | 'wx-rain'
  | 'wx-heavy'
  | 'wx-heat'
  | 'stop'
  | 'dot'
  | 'ring';

function legend(items: { label: string; kind: KeyKind }[]): HTMLElement {
  return h(
    'div',
    { class: 'pchart__legend' },
    ...items.map((i) =>
      h('span', { class: 'pchart__key' }, h('span', { class: `pchart__swatch pchart__swatch--${i.kind}` }), i.label),
    ),
  );
}

/* --------------------------- axes --------------------------- */

function yGrid(
  ticks: number[],
  y: (v: number) => number,
  x0: number,
  x1: number,
  label: (v: number) => string,
): SVGElement[] {
  return ticks.map((v) =>
    sv(
      'g',
      {},
      sv('line', { x1: x0, x2: x1, y1: r1(y(v)), y2: r1(y(v)), class: 'pchart__grid' }),
      sv('text', { x: x0 - 6, y: y(v) + 3.5, class: 'pchart__tick', 'text-anchor': 'end' }, label(v)),
    ),
  );
}

const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A tick at the first of every month the range spans, labelled by month. */
function monthAxis(start: string, lastDay: number, x: (d: number) => number, yAt: number, rule = true): SVGElement[] {
  const out: SVGElement[] = [];
  if (rule) out.push(sv('line', { x1: x(0), x2: x(lastDay), y1: yAt, y2: yAt, class: 'pchart__axis' }));
  let prevYear = '';
  for (let d = 0; d <= lastDay; d++) {
    const iso = addDays(start, d);
    if (iso.slice(8, 10) !== '01' && d !== 0) continue;
    const year = iso.slice(0, 4);
    const mon = MONTH[Number(iso.slice(5, 7)) - 1]!;
    /* The range's own first day is labelled only where it is near a month's start. */
    if (d === 0 && Number(iso.slice(8, 10)) > 10) {
      prevYear = year;
      continue;
    }
    const text = year !== prevYear && prevYear !== '' ? `${mon} ${year.slice(2)}` : mon;
    prevYear = year;
    out.push(
      sv('line', { x1: r1(x(d)), x2: r1(x(d)), y1: yAt, y2: yAt + 4, class: 'pchart__axis' }),
      sv('text', { x: x(d) + 3, y: yAt + 14, class: 'pchart__tick' }, text),
    );
  }
  return out;
}

/** Week ticks for the small multiples: the first week of each month. */
function weekAxis(weeks: number, start: string, x: (i: number) => number, yAt: number): SVGElement[] {
  const out: SVGElement[] = [sv('line', { x1: x(0) - 4, x2: x(weeks - 1) + 4, y1: yAt, y2: yAt, class: 'pchart__axis' })];
  let prev = '';
  for (let i = 0; i < weeks; i++) {
    const iso = addDays(start, i * 7);
    const mon = iso.slice(5, 7);
    if (mon === prev) continue;
    prev = mon;
    out.push(sv('text', { x: x(i), y: yAt + 14, class: 'pchart__tick', 'text-anchor': 'middle' }, MONTH[Number(mon) - 1]!));
  }
  return out;
}

/** A figure with thousands separators and at most one decimal. */
function fmt(v: number): string {
  return v.toLocaleString('en-GB', { maximumFractionDigits: 1 });
}

/** A percentage to whole numbers where it is one, a decimal where it is not. */
function fmt1(v: number): string {
  return fmt(Math.round(v * 10) / 10);
}
